import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import familyLogo from '../assets/black-family.png';
import CustomButton from '../../../components/common/CustomButton';

export function Hero() {
  const { palette, typography, breakpoints } = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('landing');
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  return (
    <Box
      id="hero"
      component="section"
      sx={{
        width: '100%',
        backgroundColor: palette.background.default,
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          pt: { xs: 14, sm: 18 },
          pb: { xs: 8, sm: 12 },
          gap: 8,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            animation: 'slideUp 1s ease',
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(40px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Stack
            spacing={3}
            useFlexGap
            sx={{
              width: '100%',
              pb: 2,
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                lineHeight: 1.25,
                fontSize: {
                  xs: typography.h3.fontSize,
                  md: typography.h1.fontSize,
                },
                fontWeight: {
                  xs: typography.h3.fontWeight,
                  md: typography.h1.fontWeight,
                },
                color: palette.primary.main,
                textAlign: { sm: 'center', md: 'left' },
              }}
            >
              {t('landing:hero.title')}
            </Typography>
            <Typography
              color="text.primary"
              component="h2"
              sx={{
                width: '100%',
                color: palette.text.primary,
                lineHeight: 1.25,
                fontSize: {
                  xs: typography.body1.fontSize,
                  md: typography.h4.fontSize,
                },
                fontWeight: {
                  xs: typography.body1.fontWeight,
                  md: typography.h3.fontWeight,
                },
                textAlign: { sm: 'center', md: 'left' },
              }}
            >
              {t('landing:hero.subTitle')}
            </Typography>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'space-around', md: 'flex-start' },
              alignItems: 'center',
              gap: { xs: 1, md: 4 },
            }}
          >
            <CustomButton
              label="Start Your Family Tree"
              color="primary"
              onClick={() => navigate('/auth/signup')}
              size={isSmallScreen ? 'small' : 'medium'}
              sx={{
                fontSize: {
                  xs: '0.8rem',
                  sm: '0.9rem',
                  md: typography.body1.fontSize,
                },
                color: palette.primary.contrastText,
                width: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
                textAlign: 'center',
              }}
            />
            <CustomButton
              label="Explore Cultural Hub"
              variant="outlined"
              color="primary"
              onClick={() => navigate('/hub')}
              size={isSmallScreen ? 'small' : 'medium'}
              sx={{
                fontSize: {
                  xs: '0.8rem',
                  sm: '0.9rem',
                  md: typography.body1.fontSize,
                },
                color: palette.primary.main,
                width: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
                textAlign: 'center',
                '&:hover': {
                  color: 'white',
                  border: 'none !important',
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: 'wrap',
              gap: { xs: 1, md: 2 },
              justifyContent: { xs: 'center', md: 'flex-start' },
              mt: 2,
            }}
          >
            {[
              'Secure & private by default',
              'Collaborate across generation',
              'Free to start',
            ].map((feature) => (
              <Box
                key={feature}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: palette.info.main,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: {
                      xs: typography.caption.fontSize,
                      md: typography.body1.fontSize,
                    },
                    color: palette.text.secondary,
                  }}
                >
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            animation: 'slideUp 1s ease',
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(40px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <img
            src={familyLogo}
            alt="beautiful family smiling"
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '20px',
            }}
          />
        </Box>
      </Container>
    </Box>
  );
}
